import React, { useReducer, useState } from 'react';
import {
  useForm,
  SubmitHandler,
  FormProvider,
} from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

import reducer, { initialState, SleepioWellnessOnboardingStep } from './sleepioWellnessOnboardingState';
import ConfirmDetails from './steps/ConfirmDetails';
import { EligibilityFormCustomization } from '../Onboarding/OnboardingFlowContext';

import CreatePassword from './steps/CreatePassword/CreatePassword';
import SleepioCoverageRejection from './steps/CoverageRejection';

import { ExpiredLinkError, PasswordNotSetError } from './steps/ErrorState';

import { ReadyToStartSleepioWellness } from './steps/ReadyToStartSleepioWellness/ReadyToStartSleepioWellness';

import { createCoverageFieldsSchema } from '@/components/CoverageFields/coverageFieldsSchema';
import { PASSWORD_VALIDATION_REGEX } from '@/utils/constants';
import { validateEmployeeId } from '@/utils/stringValidators';
import { parseSearchParams } from '@/utils/stringUtils';

const INITIAL_FORM_DATA = {
  firstName: '',
  lastName: '',
  zipCode: '',
  dateOfBirth: '',
  email: '',
  phoneNumber: '',
  password: '',
  confirmPassword: '',
  employeeId: '',
};

type WellnessOnboardingDataMap = Record<keyof typeof INITIAL_FORM_DATA, string>;

const createSchema = (errorMap: WellnessOnboardingDataMap) => createCoverageFieldsSchema(errorMap).extend({
  email: z.union([
    z.literal(''),
    z.string({ required_error: errorMap.email })
      .email(),
  ]).optional(),
  phoneNumber: z.string({
    required_error: errorMap.phoneNumber,
  }).optional(),
});

type WellnessOnboardingData = z.infer<ReturnType<typeof createSchema>>;

type Props = {
  formCustomization: EligibilityFormCustomization;
}

const SleepioWellnessOnboarding = ({
  formCustomization,
}: Props) => {
  const location = useLocation();
  // Coverage details were already collected and validated upstream (CoverageCheckWithEmail),
  // so this leg skips straight to the shared final step instead of asking for them again.
  const skipConfirmDetails = parseSearchParams(location.search).skipConfirm === '1';
  const [state, dispatch] = useReducer(reducer, skipConfirmDetails ? {
    ...initialState,
    step: 'CreatePassword',
    action: { type: 'SET_STEP', step: 'CreatePassword' },
  } : initialState);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const { t } = useTranslation();
  const {
    showZipCode,
    showEmployeeId,
    employeeIdFieldRename,
    employeeIdValidationEnabled,
    employeeIdAllowedChars,
    employeeIdMinLength,
    employeeIdMaxLength,
    employeeIdValidationErrorMessage,
  } = formCustomization;
  const employeeIdField = employeeIdFieldRename
    || t('coverage.sleepio.patient.employee_id_label');

  const errorMap: WellnessOnboardingDataMap = {
    firstName: t('coverage.errors.first_name_error'),
    lastName: t('coverage.errors.last_name_error'),
    zipCode: t('coverage.errors.zipcode_error'),
    dateOfBirth: t('coverage.errors.dob_error'),
    email: t('coverage.errors.email_error'),
    phoneNumber: t('coverage.errors.phone_error'),
    password: t('coverage.errors.password_rules_error'),
    confirmPassword: t('coverage.errors.confirm_password_error'),
    employeeId: employeeIdValidationEnabled && employeeIdValidationErrorMessage
      ? employeeIdValidationErrorMessage
      : `${t('coverage.errors.employee_id_error')} ${employeeIdField}`,
  };

  const baseSchema = createSchema(errorMap);

  const passwordSchema = z.object({
    password: z.string({ required_error: errorMap.password })
      .regex(PASSWORD_VALIDATION_REGEX, { message: t('coverage.errors.password_rules_error') }),
    confirmPassword: z.string({ required_error: errorMap.confirmPassword }),
  });

  // Same shape as baseSchema but unconstrained, since CreatePassword never fills these in.
  const looseCoverageFields = z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    zipCode: z.string().optional(),
    dateOfBirth: z.string().optional(),
    email: z.string().optional(),
    employeeId: z.string().optional(),
    phoneNumber: z.string().optional(),
  });

  // CreatePassword only renders password/confirmPassword, so it must not re-require the rest.
  const refinedSchema = state.step === 'CreatePassword'
    ? looseCoverageFields.merge(passwordSchema).refine((data) => data.password === data.confirmPassword, {
      message: errorMap.confirmPassword,
      path: ['confirmPassword'],
    })
    : baseSchema.superRefine((data, ctx) => {
      // if zip code IS shown, then require it
      if (showZipCode && !data.zipCode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['zipCode'],
          message: errorMap.zipCode,
        });
      }

      // Skip employee ID validation if not shown
      if (!showEmployeeId) {
        return data;
      }

      const employeeIdValue = data.employeeId?.trim() || '';

      // Always validate if field is required and empty
      if (!employeeIdValue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['employeeId'],
          message: errorMap.employeeId,
        });
        return data;
      }

      // Validate format only if validation is enabled AND field has value
      if (employeeIdValidationEnabled) {
        const validation = validateEmployeeId(employeeIdValue, {
          validationEnabled: employeeIdValidationEnabled,
          allowedChars: employeeIdAllowedChars,
          minLength: employeeIdMinLength,
          maxLength: employeeIdMaxLength,
          errorMessage: employeeIdValidationErrorMessage,
        });

        if (!validation.isValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['employeeId'],
            message: validation.errorMessage || errorMap.employeeId,
          });
        }
      }

      return data;
    });

  const methods = useForm<WellnessOnboardingData>({
    defaultValues: INITIAL_FORM_DATA,
    mode: 'onBlur',
    resolver: zodResolver(refinedSchema, {}, { mode: 'async' }),
  });

  const { handleSubmit } = methods;

  const onSubmit: SubmitHandler<WellnessOnboardingData> = (data) => {
    console.log(data);
  };

  const renderStep = () => {
    switch (state.step) {
    case 'ConfirmDetails':
      return (
        <ConfirmDetails
          onNext={(step: SleepioWellnessOnboardingStep) => dispatch({ type: 'SET_STEP', step })}
          formCustomization={formCustomization}
          onError={() => dispatch({ type: 'SET_STEP', step: 'ExpiredLinkError' })}
        />
      );
    case 'CreatePassword':
      return (
        <CreatePassword
          onNext={(token: string) => {
            setAuthToken(token);
            dispatch({ type: 'SET_STEP', step: 'ReadyToStartSleepioWellness' });
          }}
          onError={() => dispatch({ type: 'SET_STEP', step: 'PasswordNotSetError' })}
          isGeneric={formCustomization.isGeneric}
        />
      );
    case 'ReadyToStartSleepioWellness':
      return <ReadyToStartSleepioWellness authToken={authToken} />;
    case 'CoverageRejection':
      return <SleepioCoverageRejection />;
    case 'PasswordNotSetError':
      return <PasswordNotSetError />;
    case 'ExpiredLinkError':
      return <ExpiredLinkError />;
    default:
      return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {renderStep()}
      </form>
    </FormProvider>
  );
};

export default SleepioWellnessOnboarding;
